/**
 * `category_grid` — jump straight to a line sheet (spec 42 R2; design 43 SF3).
 *
 * Sources from collections, never `items.category`: those values are input
 * materials (fabric, trims, yarn) and would misrepresent a garment store
 * (spec 42 Q2). Tiles for collections that no longer resolve are dropped
 * rather than linked (R3).
 */
import type { TargetContext } from "./resolve-target.js";
import { resolveTarget } from "./resolve-target.js";
import { SectionImage } from "./section-image.js";

export interface CategoryTile {
  slug: string;
  name: string;
  imageUrl: string | null;
  listingCount: number;
}

export function CategoryGrid({
  heading,
  subheading,
  tiles,
  style,
  showCounts,
  ctx,
}: {
  heading: string;
  subheading?: string | undefined;
  tiles?: CategoryTile[];
  style: "cards" | "circles";
  showCounts: boolean;
  ctx: TargetContext;
}) {
  const live = (tiles ?? []).filter(
    (t) => resolveTarget({ kind: "collection", slug: t.slug }, ctx) !== null,
  );
  if (live.length === 0) return null;

  return (
    <section aria-label={heading}>
      <div className="flint-section-head">
        <div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "1.5rem",
              color: "var(--ink)",
              margin: 0,
            }}
          >
            {heading}
          </h2>
          {subheading ? (
            <p style={{ fontSize: "0.875rem", color: "var(--muted)", marginTop: "0.25rem" }}>
              {subheading}
            </p>
          ) : null}
        </div>
      </div>

      {style === "circles" ? (
        <div className="flint-scroll-track">
          {live.map((t) => (
            <a
              key={t.slug}
              href={`/s/${ctx.storeSlug}/c/${t.slug}`}
              className="flint-circle-tile"
              style={{ color: "var(--ink)", textDecoration: "none" }}
            >
              <div className="flint-circle-img">
                <SectionImage src={t.imageUrl} alt="" aspectRatio="1 / 1" radius="999px" />
              </div>
              <span style={{ fontSize: "0.8125rem", textAlign: "center" }}>{t.name}</span>
            </a>
          ))}
        </div>
      ) : (
        <div className="flint-card-grid">
          {live.map((t) => (
            <a
              key={t.slug}
              href={`/s/${ctx.storeSlug}/c/${t.slug}`}
              style={{
                display: "block",
                border: "1px solid var(--line)",
                borderRadius: "var(--r)",
                overflow: "hidden",
                textDecoration: "none",
                backgroundColor: "var(--paper)",
                position: "relative",
              }}
            >
              <SectionImage src={t.imageUrl} alt="" aspectRatio="4 / 3" radius="0" />
              {showCounts ? (
                <span
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    left: "0.5rem",
                    backgroundColor: "var(--paper)",
                    color: "var(--ink)",
                    borderRadius: 999,
                    padding: "0.125rem 0.5rem",
                    fontSize: "0.75rem",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {t.listingCount}
                </span>
              ) : null}
              <div style={{ padding: "0.75rem" }}>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 500,
                    color: "var(--ink)",
                  }}
                >
                  {t.name}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>Shop wholesale</div>
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
